import React, { useEffect, useState } from "react";

import { Headline, Label, Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  OrganizationNotificationSettingsType,
  useUpdateOrgNotificationSettingsMutation,
} from "@api/graphql/types-and-hooks";
import { ToggleSwitch } from "@components/ToggleSwitch/ToggleSwitch";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { useAdminData } from "@providers/AdminDataProvider";

export const OrgManageNotificationsPage: React.FC = () => {
  const { orgnizationDetailsQuery } = useAdminData();
  const notificationSettings = orgnizationDetailsQuery.data?.getUserOrgnizationDetails?.notificationSettings;

  const [settings, setSettings] = useState<OrganizationNotificationSettingsType>();

  useEffect(() => {
    if (notificationSettings) {
      setSettings(notificationSettings as OrganizationNotificationSettingsType);
    }
  }, [notificationSettings]);

  const updateMutation = useUpdateOrgNotificationSettingsMutation({
    onSuccess: () => {
      notifySuccess("Notification settings updated successfully");
    },
    onError: (error) => {
      setSettings(settings);
      notifyError("Failed to update notification settings");
      console.error(error);
    },
  });

  const handleToggle = async (field: keyof OrganizationNotificationSettingsType, enabled: boolean) => {
    // optimistically update the state
    const updatedSettings = { ...settings, [field]: enabled };
    setSettings(updatedSettings as OrganizationNotificationSettingsType);

    // update settings
    let response;
    try {
      response = await updateMutation.mutateAsync({
        input: {
          reassessmentReminder: updatedSettings.reassessmentReminder || false,
        },
      });
    } catch (error) {
      setSettings(settings);
      notifyError("Failed to update notification settings");
      console.error(error);
      return;
    }

    if (!response.updateOrgNotificationSettings?.success) {
      setSettings(settings);
      notifyError(response.updateOrgNotificationSettings?.message || "Failed to update notification settings.");
      return;
    }

    // refetch org settings
    orgnizationDetailsQuery.refetch();
  };

  return (
    <div className="flex flex-col w-full">
      <Headline size="medium" className="mt-10">
        Manage Organization-wide Notifications
      </Headline>
      <Paragraph colorType="secondary" className="mt-2">
        Control which notifications your organization receives from Frontera
      </Paragraph>

      <br />
      <Label displayType="normal" size="small" colorType="secondary">
        Reassessment
      </Label>
      <div className="flex flex-row gap-4 items-center pt-4">
        <ToggleSwitch
          enabled={settings?.reassessmentReminder || false}
          setEnabled={(enabled) => handleToggle("reassessmentReminder", enabled)}
        />
        <Paragraph>Reassessment reminder (45 days before due date)</Paragraph>
      </div>
    </div>
  );
};
